/** @format */

import { createReducer, on } from '@ngrx/store';
import {
	featureConfigCustomBadgesSet,
	productListContainerFeatureFlagsSet,
	productListResolverFeatureFlagsSet,
	removeAllFilters,
	setAllFilters,
	setCriteoProducts,
	setDisplayableProductListData,
	setFiltering,
	setHomrBannerData,
	setInitialPageNumber,
	setInitialPageSize,
	setIsInitialPageLoad,
	setIsProductListPageLoading,
	setIsProductListPageValid,
	setMlBypass,
	setPageNumber,
	setPageSize,
	setPageSizeAndPageNumber,
	setPinnedContent,
	setPiqProducts,
	setProductAvailability,
	setProductList,
	setProductListPromo,
	setProductListSEO,
	setRefinementUpdateObj,
	setSelectedSort,
	setSkeletonLoader,
	setSnbAudience,
	updateSelectedSort
} from './product-list.action';

import { DEFAULT_PAGE_SIZE } from '@plp/data-access/constants';
import { EmptyHomrBannerData } from '@plp/data-access/mocks';
import { ProductListCore } from '@plp/data-access/interfaces';

export const initialProductListState: ProductListCore = {
	displayableProductListData: [],
	fallbackSearchParams: {},
	featureFlags: {},
	homrBannerData: EmptyHomrBannerData,
	isInitialPageLoad: true,
	isProductListPageLoading: true,
	isProductListPageValid: true,
	isSkeletonLoaderVisible: false,
	mlBypass: false,
	pageNumber: 0,
	pageSize: DEFAULT_PAGE_SIZE,
	pinnedContent: null,
	piqProducts: null,
	criteoProducts: null,
	productAvailability: null,
	productList: null,
	productListSEO: null,
	productPromotion: null,
	refinementUpdateObj: undefined,
	selectedFilters: {},
	selectedSort: { label: '', value: 0 },
	snbAudience: '',
	customBadges: {}
};

export const productListReducers = createReducer(
	// Supply the initial state
	initialProductListState,
	on(setProductListSEO, (state, { productListSEO }) => ({
		...state,
		productListSEO
	})),
	on(setProductList, (state, { productList }) => {
		// Scenario: If the search term has a typo or missing letter and mlSpelling is true, then didYouMeanVO will have suggestions.
		// If didYouMeanVO is present, then update the search term with the suggestion
		if (productList?.didYouMeanVO?.suggestions?.length && !state.mlBypass) {
			const dym: string = productList.didYouMeanVO.suggestions[0];
			return {
				...state,
				productList: {
					...productList,
					searchVO: {
						...productList.searchVO,
						searchTerm: dym
					}
				}
			};
		}

		return {
			...state,
			productList
		};
	}),
	on(setProductAvailability, (state, { productAvailability }) => ({
		...state,
		productAvailability
	})),
	on(setProductListPromo, (state, { productPromotion }) => ({
		...state,
		productPromotion
	})),
	on(setPinnedContent, (state, { pinnedContent }) => ({
		...state,
		pinnedContent
	})),
	on(setPiqProducts, (state, { piqProducts }) => ({
		...state,
		piqProducts
	})),
	on(setCriteoProducts, (state, { criteoProducts }) => ({
		...state,
		criteoProducts
	})),
	on(setSelectedSort, (state, { selectedSort }) => ({
		...state,
		selectedSort,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		}
	})),
	on(updateSelectedSort, (state, { selectedSort }) => ({
		...state,
		selectedSort,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		},
		pageNumber: 0
	})),
	on(setPageNumber, (state, { pageNumber }) => ({
		...state,
		pageNumber,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		}
	})),
	on(setPageSize, (state, { pageSize }) => ({
		...state,
		pageSize,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		}
	})),
	on(setPageSizeAndPageNumber, (state, { pageSize, pageNumber }) => ({
		...state,
		pageSize,
		pageNumber,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		}
	})),
	on(setHomrBannerData, (state, { homrBannerData }) => ({
		...state,
		homrBannerData
	})),
	on(setIsProductListPageLoading, (state, { isProductListPageLoading }) => ({
		...state,
		isProductListPageLoading
	})),
	on(setIsProductListPageValid, (state, { isProductListPageValid }) => ({
		...state,
		isProductListPageValid
	})),
	on(setIsInitialPageLoad, (state, { isInitialPageLoad }) => ({
		...state,
		isInitialPageLoad
	})),
	on(setInitialPageNumber, (state, { pageNumber }) => ({
		...state,
		pageNumber
	})),
	on(setInitialPageSize, (state, { pageSize }) => ({
		...state,
		pageSize
	})),
	on(setSnbAudience, (state, { snbAudience }) => ({
		...state,
		snbAudience
	})),
	on(setDisplayableProductListData, (state, { displayableProductListData }) => ({
		...state,
		displayableProductListData
	})),
	on(setSkeletonLoader, (state, { isSkeletonLoaderVisible }) => ({
		...state,
		isSkeletonLoaderVisible
	})),
	on(
		productListContainerFeatureFlagsSet,
		productListResolverFeatureFlagsSet,
		(state, { featureFlags }) => ({
			...state,
			featureFlags
		})
	),
	on(setAllFilters, (state, { selectedFilters }) => ({
		...state,
		selectedFilters
	})),
	on(setFiltering, (state, { selectedFilters }) => {
		for (const filterKey in selectedFilters) {
			if (Object.values(selectedFilters[filterKey]).length > 0) {
				// Adds specific filter type in the store with the new filter values.
				// Example selectedFilters: {X_Brand: ['NIKE'], gender: ['Mens', 'Womens']}
				return {
					...state,
					selectedFilters: {
						...state.selectedFilters,
						...selectedFilters
					},
					pageNumber: 0,
					fallbackSearchParams: {
						filters: state.selectedFilters,
						pageNumber: state.pageNumber,
						pageSize: state.pageSize,
						selectedSort: state.selectedSort
					}
				};
			} else {
				// Removes specific filter type in the store if filter value is empty.
				// Example REMOVES filter option(GENDER) from object seen above. selectedFilters: {X_Brand: ['NIKE']}
				const updatedSelectedFilters = { ...state.selectedFilters };
				delete updatedSelectedFilters[filterKey];
				return {
					...state,
					selectedFilters: updatedSelectedFilters,
					pageNumber: 0,
					fallbackSearchParams: {
						filters: state.selectedFilters,
						pageNumber: state.pageNumber,
						pageSize: state.pageSize,
						selectedSort: state.selectedSort
					}
				};
			}
		}
	}),
	on(removeAllFilters, (state) => ({
		...state,
		selectedFilters: {},
		pageNumber: 0,
		fallbackSearchParams: {
			filters: state.selectedFilters,
			pageNumber: state.pageNumber,
			pageSize: state.pageSize,
			selectedSort: state.selectedSort
		}
	})),
	on(setMlBypass, (state, { mlBypass }) => ({
		...state,
		mlBypass: mlBypass
	})),
	on(setRefinementUpdateObj, (state, { refinementUpdateObj }) => ({
		...state,
		refinementUpdateObj: refinementUpdateObj
	})),
	on(featureConfigCustomBadgesSet, (state, { customBadges }) => ({
		...state,
		customBadges
	}))
);

export function productListReducer(state, action) {
	return productListReducers(state, action);
}
